import { useAction } from "convex/react"
import { useCallback } from "react"
import { api } from "~convex/_generated/api"
import type { Id } from "~convex/_generated/dataModel"
import { parsePayCommand } from "~shared/parsePayCommand"
import { useSolanaSign } from "./useSolanaSign"

/**
 * Fire-and-forget client-side mention-pay for the web app.
 * Parses post content for `@handle $N USDC`, prepares an unsigned tx on the
 * server, signs locally with the embedded wallet, then commits + confirms.
 */
export function useMentionPay() {
	const prepareMentionPay = useAction(api.send.prepareMentionPay)
	const commitMentionPay = useAction(api.send.commitMentionPay)
	const { signTransaction, isReady } = useSolanaSign()

	const executeMentionPay = useCallback(
		async (content: string, postId: Id<"posts">) => {
			const cmd = parsePayCommand(content)
			if (!cmd || !isReady) {
				return
			}

			try {
				const prepared = await prepareMentionPay({
					recipientHandle: cmd.handle,
					amountUsd: cmd.amountUsd,
					postId,
				})
				const signedTx = await signTransaction(prepared.tx)
				await commitMentionPay({
					prepare_token: prepared.prepareToken,
					signed_tx: signedTx,
				})
			} catch {
				// Silently fail — post exists, payment is best-effort
			}
		},
		[commitMentionPay, isReady, prepareMentionPay, signTransaction],
	)

	return { executeMentionPay }
}
